import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'universal-cookie';
import { jsx as _jsx } from "react/jsx-runtime";
const CookiesContext = /*#__PURE__*/createContext(new Cookies());

/**
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {import('universal-cookie').default} [props.cookies]
 * @returns
 */
export function CookiesProvider(_ref) {
  let {
    cookies,
    children
  } = _ref;
  const [cookie] = useState(() => {
    if (cookies) {
      return cookies;
    } else {
      return new Cookies();
    }
  });
  return /*#__PURE__*/_jsx(CookiesContext.Provider, {
    value: cookie,
    children: children
  });
}

// inspired from react-cookie

/**
 *
 * @param {string[]} [dependencies]
 * @returns {{
 *  cookies: { [name: string]: any },
 *  setCookie: (name: string, value: import('universal-cookie').Cookie, options?: import('universal-cookie').CookieSetOptions) => void,
 *  removeCookie: (name: string, options?: import('universal-cookie').CookieSetOptions) => void
 * }}
 */
export function useCookie(dependencies) {
  const cookies = useContext(CookiesContext);
  if (!cookies) {
    throw new Error('Missing <CookiesProvider>');
  }
  const initialCookies = cookies.getAll();
  const [allCookies, setCookies] = useState(initialCookies);
  const previousCookiesRef = useRef(allCookies);
  const dependenciesRef = useRef(dependencies);
  useEffect(() => {
    dependenciesRef.current = dependencies;
  });
  useEffect(() => {
    function onChange() {
      const newCookies = cookies.getAll();
      if (shouldUpdate(dependenciesRef.current || null, newCookies, previousCookiesRef.current)) {
        setCookies(newCookies);
      }
      previousCookiesRef.current = newCookies;
    }
    cookies.addChangeListener(onChange);
    return () => {
      cookies.removeChangeListener(onChange);
    };
  }, [cookies]);
  const setCookie = useMemo(() => cookies.set.bind(cookies), [cookies]);
  const removeCookie = useMemo(() => cookies.remove.bind(cookies), [cookies]);
  return {
    cookies: allCookies,
    setCookie,
    removeCookie
  };
}

/**
 *
 * @param {string[] | null} dependencies
 * @param {{[name: string]: any}} newCookies
 * @param {{[name: string]: any}} oldCookies
 * @returns
 */
function shouldUpdate(dependencies, newCookies, oldCookies) {
  if (!dependencies) {
    return true;
  }
  for (let dependency of dependencies) {
    if (newCookies[dependency] !== oldCookies[dependency]) {
      return true;
    }
  }
  return false;
}